// Attention signals for runs that change state while you're elsewhere:
// a browser notification, an unread count in the tab title, and an optional
// short chime. All opt-in and per-browser.
import { ref, watch } from "vue";

const PREFS_KEY = "rushd.mc.notify";

interface Prefs {
  browser: boolean;
  chime: boolean;
}

function loadPrefs(): Prefs {
  try {
    return { browser: false, chime: false, ...JSON.parse(localStorage.getItem(PREFS_KEY) ?? "{}") };
  } catch {
    return { browser: false, chime: false };
  }
}

const prefs = ref<Prefs>(loadPrefs());
const unread = ref(0);
const baseTitle = typeof document !== "undefined" ? document.title : "Majlis";

watch(prefs, (p) => {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    // ignore — prefs just won't persist
  }
}, { deep: true });

watch(unread, (n) => {
  if (typeof document !== "undefined") document.title = n > 0 ? `(${n}) ${baseTitle}` : baseTitle;
});

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") unread.value = 0;
  });
}

let audio: AudioContext | null = null;

// Two soft sine notes; quiet enough for an open-plan office.
function chime(good: boolean): void {
  try {
    audio ??= new AudioContext();
    const now = audio.currentTime;
    const notes = good ? [660, 880] : [440, 330];
    notes.forEach((freq, i) => {
      const osc = audio!.createOscillator();
      const gain = audio!.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + i * 0.14);
      gain.gain.exponentialRampToValueAtTime(0.08, now + i * 0.14 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.14 + 0.3);
      osc.connect(gain).connect(audio!.destination);
      osc.start(now + i * 0.14);
      osc.stop(now + i * 0.14 + 0.32);
    });
  } catch {
    // Web Audio unavailable — silent is fine
  }
}

export interface Toast {
  id: number;
  title: string;
  body: string;
  tone: "good" | "bad" | "info";
  href?: string;
}

const toasts = ref<Toast[]>([]);
let toastSeq = 0;

export function useNotifications() {
  const permission = ref<NotificationPermission | "unsupported">(
    typeof Notification === "undefined" ? "unsupported" : Notification.permission,
  );

  async function enableBrowser(): Promise<void> {
    if (typeof Notification === "undefined") return;
    permission.value = await Notification.requestPermission();
    prefs.value.browser = permission.value === "granted";
  }

  function dismiss(id: number): void {
    toasts.value = toasts.value.filter((t) => t.id !== id);
  }

  function notify(t: Omit<Toast, "id">): void {
    const id = ++toastSeq;
    toasts.value = [...toasts.value.slice(-3), { ...t, id }];
    setTimeout(() => dismiss(id), 7000);
    const hidden = typeof document !== "undefined" && document.visibilityState === "hidden";
    if (hidden) unread.value += 1;
    if (prefs.value.chime) chime(t.tone !== "bad");
    if (prefs.value.browser && hidden && typeof Notification !== "undefined" && Notification.permission === "granted") {
      const n = new Notification(t.title, { body: t.body, icon: "/favicon-48.png", tag: t.href });
      const href = t.href;
      // The router is loaded on click: importing it here would be an import cycle.
      if (href) n.onclick = () => { window.focus(); void import("@/router").then(({ router }) => router.push(href)); n.close(); };
    }
  }

  return { prefs, permission, toasts, unread, enableBrowser, notify, dismiss };
}
